import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArticleForm } from './ArticleForm';

export const Route = createFileRoute('/a/articles/$articleId')({
  component: ArticleEditPage,
});

/** `$articleId` is the literal `new` to create an article, otherwise an article id to edit. */
function ArticleEditPage() {
  const { articleId } = Route.useParams();

  if (articleId === 'new') {
    return <ArticleForm key="new" />;
  }

  return <EditExistingArticle articleId={articleId} />;
}

function EditExistingArticle({ articleId }: { articleId: string }) {
  const convex = useConvex();
  const navigate = useNavigate();
  const { data: articles, isPending, isError, error } = useQuery(api.articles.list);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await convex.mutation(api.articles.remove, { articleId });
      toast.success('Article deleted');
      setIsDeleteDialogOpen(false);
      void navigate({ to: '/a/articles' });
    } catch (err) {
      toast.error('Unable to delete article', { description: getUserErrorMessage(err) });
    }
    setIsDeleting(false);
  };

  if (isError) return <ErrorAlert message={error.message} title="Error loading article" />;
  if (isPending) return <LoadingIcon />;

  const article = articles.find((a) => a._id === articleId);
  if (!article) return <ErrorAlert message="This article no longer exists." title="Not found" />;

  return (
    <>
      <ArticleForm
        article={article}
        key={article._id}
        onDelete={() => setIsDeleteDialogOpen(true)}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete '${article.title}'?`}</AlertDialogTitle>
            <AlertDialogDescription>
              The article will be removed from the knowledge base and its search index. This can't
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isDeleting} onClick={handleDelete} variant="destructive">
              Permanently delete
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
